// types
import type { StrapiSchemaSettings, StrapiSiteSeo } from '@ts-strapi/interfaces'
import type { JsonLdNode } from '@ts-structured-data/types'
// helpers
import { resolveStrapiMediaUrl } from '@helpers/strapi-media'

interface BuildOrganizationOptions {
  siteSeo: StrapiSiteSeo | null
  settings: StrapiSchemaSettings | null
  origin: string
  strapiPublicUrl: string
}

export const buildOrganization = (
  { siteSeo, settings, origin, strapiPublicUrl }: BuildOrganizationOptions,
): JsonLdNode | null => {
  if (!siteSeo) return null

  const node: JsonLdNode = {
    '@type': settings?.organizationType ?? 'EducationalOrganization',
    '@id': `${origin}/#organization`,
    name: siteSeo.organizationName,
    url: `${origin}/`,
  }

  if (settings?.alternateName) node.alternateName = settings.alternateName
  if (settings?.description) node.description = settings.description
  if (settings?.award) node.award = settings.award
  if (siteSeo.contactEmail) node.email = siteSeo.contactEmail

  const logo = siteSeo.organizationLogo
  if (logo) {
    const logoNode: JsonLdNode = {
      '@type': 'ImageObject',
      '@id': `${origin}/#logo`,
      url: resolveStrapiMediaUrl(logo.url, strapiPublicUrl),
    }
    if (logo.width) logoNode.width = logo.width
    if (logo.height) logoNode.height = logo.height
    node.logo = logoNode
    node.image = { '@id': `${origin}/#logo` }
  }

  const address = settings?.address
  if (address && (address.streetAddress || address.addressLocality)) {
    node.address = {
      '@type': 'PostalAddress',
      ...(address.streetAddress ? { streetAddress: address.streetAddress } : {}),
      ...(address.addressLocality ? { addressLocality: address.addressLocality } : {}),
      ...(address.addressRegion ? { addressRegion: address.addressRegion } : {}),
      ...(address.postalCode ? { postalCode: address.postalCode } : {}),
      ...(address.addressCountry ? { addressCountry: address.addressCountry } : {}),
    }
  }

  const contactPoints = settings?.contactPoints ?? []
  if (contactPoints.length) {
    node.contactPoint = contactPoints.map(cp => ({
      '@type': 'ContactPoint',
      email: cp.email,
      contactType: cp.contactType,
    }))
  }
  else if (siteSeo.contactEmail) {
    node.contactPoint = {
      '@type': 'ContactPoint',
      email: siteSeo.contactEmail,
      contactType: 'customer support',
    }
  }

  if (siteSeo.sameAs?.length) node.sameAs = siteSeo.sameAs.map(item => item.url)

  return node
}
